import { useRef, useState } from 'react'
import { Fingerprint, ShieldCheck, X, KeyRound } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { useWebAuthn } from '../hooks/useWebAuthn'
import { showToast } from './Toast'

// #838: passkey ceremony が「対応・OS 設定可だが *フリーズ*」する実機が
// 存在する(promise が resolve も reject もしない)。catch だけだと escape
// が永遠に出ず dead-end。試行後この時間応答が無ければ escape を解禁する
// (passkey ceremony は中断しない=非破壊。成功すればそのまま完了)。
const FREEZE_REVEAL_MS = 20000

interface Props {
  open: boolean
  /** required mode のとき close を許さない (= 強制 enrollment)。default false (= dismissible). */
  enforced?: boolean
  onClose: () => void
  /** 登録成功時の callback (= 上位で次画面へ遷移したいケース) */
  onEnrolled?: (credentialId: string) => void
  /**
   * #838: passkey ceremony が **失敗した時のみ** TOTP 設定へ退避する導線。
   * `TOTP_FALLBACK_ENABLED` ON のとき RequireAuth が渡す。**事前の対等
   * 選択にはしない**(#789: WebAuthn 主・TOTP は sanctioned fallback)。
   * 「対応判定 true でも実機で passkey を作れない層」の dead-end 解消。
   * 未指定(= flag OFF)では escape を一切表示しない = byte-for-byte 不変。
   */
  onFallbackToTotp?: () => void
}

/**
 * Passkey 登録モーダル (#734).
 *
 * 表示パターン:
 *   - 新規登録時 (required_new_users 以上) → enforced=true
 *   - 既存ユーザーの promotion (optional / required_new_users) → enforced=false (dismissible)
 *
 * ブラウザ非対応 (WebAuthn unsupported) の場合は警告を表示 + dismissible に。
 */
export default function PasskeyEnrollmentModal({
  open,
  enforced = false,
  onClose,
  onEnrolled,
  onFallbackToTotp,
}: Props) {
  const { t } = useI18n()
  const { supported, enroll } = useWebAuthn()
  const [submitting, setSubmitting] = useState(false)
  // #838: passkey ceremony が「失敗」or「freeze(無応答)」したら escape を
  // 解禁する。事前には出さない(#789: 対等選択にしない・WebAuthn 主)。
  // NotAllowedError は user cancel と device 不能の両方で出て区別不能、かつ
  // freeze は catch すら走らないため、「試行後に失敗 or 無応答」を escape の
  // 解禁条件とするのが最も堅牢。
  const [attemptFailed, setAttemptFailed] = useState(false)
  const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  if (!open) return null

  const clearRevealTimer = () => {
    if (revealTimerRef.current) {
      clearTimeout(revealTimerRef.current)
      revealTimerRef.current = null
    }
  }

  const handleEnroll = async () => {
    if (submitting) return
    setSubmitting(true)
    // #838 freeze 対策: ceremony が FREEZE_REVEAL_MS 無応答なら escape を
    // 解禁 + 再試行可に戻す(enroll() は中断しない。後で成功すれば try が
    // そのまま完了させる)。成功/失敗で必ず clear。
    clearRevealTimer()
    revealTimerRef.current = setTimeout(() => {
      setAttemptFailed(true)
      setSubmitting(false)
    }, FREEZE_REVEAL_MS)
    try {
      const result = await enroll()
      clearRevealTimer()
      showToast({ type: 'success', message: t('passkey.enrolled') })
      // #1114 B: 登録直後に「同期型/端末バインド型」と意味を明示する。
      // synced(multi_device)=複数端末で使えるが端末復旧に使えない。
      // 端末バインド(single_device)=この端末専用・端末復旧に使える。
      if (result.credentialDeviceType === 'multi_device') {
        showToast({ type: 'info', message: t('passkey.enrolledSynced'), duration: 8000 })
      } else if (result.credentialDeviceType === 'single_device') {
        showToast({ type: 'info', message: t('passkey.enrolledDeviceBound'), duration: 8000 })
      }
      onEnrolled?.(result.credentialId)
      onClose()
    } catch (e) {
      clearRevealTimer()
      const name = (e as Error)?.name
      console.error('[PasskeyEnrollment] failed:', e)
      // ユーザーがキャンセル (= Stop ボタン押下) は silent
      if (name === 'NotAllowedError' || name === 'AbortError') {
        showToast({ type: 'info', message: t('passkey.cancelled') })
      } else {
        showToast({ type: 'error', message: t('passkey.enrollFailed') })
      }
      // #838: 失敗 = 「対応判定 true でも実機で作れない」可能性。escape を解禁
      // (onFallbackToTotp 未指定 = flag OFF では何も起きない = 挙動不変)。
      setAttemptFailed(true)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 animate-scale-in">
        {!enforced && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
            aria-label="close"
          >
            <X size={16} className="text-text-secondary" />
          </button>
        )}

        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-yokoso-red/10">
          <Fingerprint size={32} className="text-yokoso-red" />
        </div>

        <h3 className="font-display text-xl font-bold text-text-primary text-center mb-2">
          {t('passkey.enrollTitle')}
        </h3>
        <p className="font-body text-sm text-text-secondary text-center mb-5 leading-relaxed">
          {t('passkey.enrollDescription')}
        </p>

        {/* メリット bullets */}
        <ul className="space-y-2 mb-5">
          <li className="flex items-start gap-2 text-sm font-body text-text-primary">
            <ShieldCheck size={16} className="text-matcha shrink-0 mt-0.5" />
            {t('passkey.benefit1')}
          </li>
          <li className="flex items-start gap-2 text-sm font-body text-text-primary">
            <ShieldCheck size={16} className="text-matcha shrink-0 mt-0.5" />
            {t('passkey.benefit2')}
          </li>
          <li className="flex items-start gap-2 text-sm font-body text-text-primary">
            <ShieldCheck size={16} className="text-matcha shrink-0 mt-0.5" />
            {t('passkey.benefit3')}
          </li>
        </ul>

        {!supported ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-4">
            <p className="text-xs font-body text-amber-900 leading-relaxed">
              {t('passkey.unsupported')}
            </p>
          </div>
        ) : null}

        <button
          onClick={handleEnroll}
          disabled={!supported || submitting}
          className={`w-full h-12 rounded-xl font-display font-bold text-sm transition-colors ${
            !supported || submitting ? 'bg-gray-300 text-white cursor-not-allowed' : 'bg-yokoso-red text-white tap-effect'
          }`}
        >
          {submitting ? '...' : t('passkey.enrollAction')}
        </button>

        {/* #838: passkey が失敗した時のみ TOTP 退避を提示(事前には出さない
            = #789 整合)。onFallbackToTotp 未指定(flag OFF)では非表示。 */}
        {attemptFailed && onFallbackToTotp && (
          <>
            <div className="flex items-center gap-3 my-3">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-[11px] font-body text-text-tertiary">
                {t('totp.orSeparator')}
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            <button
              onClick={onFallbackToTotp}
              className="flex items-center justify-center gap-2 w-full h-12 rounded-xl border border-yokoso-red/50 bg-yokoso-red/5 text-yokoso-red font-display font-bold text-sm tap-effect"
            >
              <KeyRound size={16} />
              {t('passkey.totpFallbackAction')}
            </button>
            <p className="font-body text-[11px] text-text-secondary text-center mt-2 leading-relaxed">
              {t('passkey.totpFallbackHint')}
            </p>
          </>
        )}

        {!enforced && (
          <button
            onClick={onClose}
            className="w-full h-10 mt-2 text-xs font-body text-text-secondary tap-effect"
          >
            {t('passkey.skipForNow')}
          </button>
        )}
      </div>
    </div>
  )
}
